/* Frameworks and evidence packs, over the real API.
 *
 * No `X-Lumiq-Tenant` header, here or anywhere — hard rule 1.
 *
 * There is no update or delete mutation because the server has none: a framework version is
 * immutable and a pack is the evidence, both INSERT/SELECT-only at the grant.
 *
 * **The download is an authenticated fetch, not a plain href — and it used to be a plain href, which
 * did not work.** The old reasoning here was that the body must be the stored bytes verbatim for the
 * checksum to hold and that "routing it through fetch-and-blob would invite a re-serialization". The
 * second half is simply false: `Response.blob()` hands back the bytes the server sent, unexamined.
 * The first half is right and is now *proved* rather than asserted — `downloadPack` verifies the
 * SHA-256 of what arrived against the checksum on the row.
 *
 * What the anchor actually did was 401. A browser navigation carries no `Authorization` header, and
 * under ADR-012's local auth the bearer token lives in this process's memory — so clicking Download
 * replaced the app with the API's own `UnauthenticatedError` problem document. Every other read on
 * this screen went through `shared/api/http`, which attaches the token; the one anchor did not.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isDatasetCoverage,
  isEvidencePack,
  isFramework,
} from '@/shared/api/generated/compliance';
import type {
  DatasetCoverage,
  EvidencePack,
  Framework,
} from '@/shared/api/generated/compliance';

import { filenameFrom, getBytes, getOne, getPage, post } from '@/shared/api/http';

import { orderPacks } from '../model/packs';

export const FRAMEWORKS_URL = '/v1/compliance/frameworks';
export const PACKS_URL = '/v1/compliance/packs';

export function downloadUrl(packId: string): string {
  return `${PACKS_URL}/${encodeURIComponent(packId)}/download`;
}

const FRAMEWORKS_KEY = ['compliance', 'frameworks'] as const;
const PACKS_KEY = ['compliance', 'packs'] as const;

/* The transport is `@/shared/api/http`. The local re-implementation this replaced unwrapped the
 * envelope and guarded every item exactly as `getPage` does — and omitted the bearer token, so this
 * screen 401'd under ADR-012's local auth. The refuse-the-whole-page rule below is `getPage`'s own,
 * so the behaviour is unchanged and the reasoning now lives in one place. */
async function fetchItems<T>(
  url: string,
  guard: (value: unknown) => value is T,
  what: string,
): Promise<readonly T[]> {
  return (await getPage(url, guard, what)).items;
}

export function useFrameworks(): UseQueryResult<readonly Framework[]> {
  return useQuery({
    queryKey: FRAMEWORKS_KEY,
    queryFn: () => fetchItems(FRAMEWORKS_URL, isFramework, 'compliance framework'),
    retry: false,
  });
}

export function usePacks(): UseQueryResult<readonly EvidencePack[]> {
  return useQuery({
    queryKey: PACKS_KEY,
    queryFn: async () =>
      orderPacks([...(await fetchItems(PACKS_URL, isEvidencePack, 'evidence pack'))]),
    retry: false,
  });
}

export interface GenerateRequest {
  readonly frameworkCode: string;
  readonly frameworkVersion: string;
  readonly periodStart: string;
  readonly periodEnd: string;
}

/* One key per (framework, period), minted at render. A double-click through a slow network retries
 * the same generation rather than creating two packs; changing the period or the framework is a
 * different decision and gets a different key. A *deliberate* re-generation over the same inputs is
 * the one case this blocks — refresh the page, which is the honest cost of making the default safe. */
const keys = new Map<string, string>();

export function idempotencyKeyFor(request: GenerateRequest): string {
  const slot = `${request.frameworkCode}@${request.frameworkVersion}:${request.periodStart}:${request.periodEnd}`;
  const existing = keys.get(slot);
  if (existing !== undefined) return existing;
  const minted = `pack-${crypto.randomUUID()}`;
  keys.set(slot, minted);
  return minted;
}

async function generate(request: GenerateRequest): Promise<EvidencePack> {
  return post(PACKS_URL, idempotencyKeyFor(request), isEvidencePack, 'evidence pack', {
    framework_code: request.frameworkCode,
    framework_version: request.frameworkVersion,
    period_start: request.periodStart,
    period_end: request.periodEnd,
  });
}

export function useGeneratePack(): UseMutationResult<
  EvidencePack,
  Error,
  GenerateRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: generate,
    onSettled: () => client.invalidateQueries({ queryKey: PACKS_KEY }),
  });
}

export interface RegisterRequest {
  readonly code: string;
  readonly version: string;
  readonly title: string;
  readonly description: string;
  readonly controls: readonly {
    readonly code: string;
    readonly title: string;
    readonly evidence: readonly string[];
  }[];
}

/** Register one framework version.
 *
 * **Idempotent on `(code, version)` at the server**, so the key is minted from that pair rather than
 * per submission: a retried POST through a slow network re-registers the same definition and gets the
 * stored row back, where a fresh key each time would turn one refusal into two attempts. A *different*
 * definition under an existing version is a 409 — a version is immutable because packs embed it — and
 * that refusal is shown verbatim rather than restated here.
 *
 * `description` is sent on the framework and omitted on each control. The contract makes both
 * optional; the form requires the framework's, because the table's second column is "what it decides"
 * and a blank there is a row nobody can act on. A control's is left absent rather than sent as `''`,
 * so the detail screen's "no description was registered" branch is reached honestly — an empty string
 * is a value somebody typed, and absent is a field nobody filled.
 */
export function useRegisterFramework(): UseMutationResult<
  Framework,
  Error,
  RegisterRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (request: RegisterRequest) =>
      post(
        FRAMEWORKS_URL,
        idempotencyKeyFor({
          frameworkCode: request.code,
          frameworkVersion: request.version,
          periodStart: 'register',
          periodEnd: 'register',
        }),
        isFramework,
        'compliance framework',
        {
          code: request.code,
          version: request.version,
          title: request.title,
          description: request.description,
          controls: request.controls.map((control) => ({
            code: control.code,
            title: control.title,
            evidence: control.evidence,
          })),
        },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: FRAMEWORKS_KEY });
      /* Not the packs: registering a framework evidences nothing on its own, so nothing about what
       * has been generated can have changed. */
    },
  });
}

/** What a download attempt did.
 *
 * `verified` and `unverified` are both successes — the file is saved either way. The distinction is
 * whether the bytes could be checked against the recorded checksum, which needs `crypto.subtle` and
 * therefore a secure context: `localhost` and HTTPS have it, plain HTTP on a hostname does not. A
 * deployment without it is not broken; the auditor still verifies offline, which is the design.
 *
 * `mismatched` is the one that matters. It means the stored bytes hashed to something other than the
 * checksum recorded when they were written — object storage returned something other than what was
 * put there, which is precisely what content-addressing exists to catch.
 */
export type DownloadOutcome = 'verified' | 'unverified' | 'mismatched';

/** The filename the server named, or one derived from the pack id.
 *
 * Parsed rather than assumed, because the server owns it: `Content-Disposition: attachment;
 * filename="evidence-pack-<id>.json"`. Deriving it here would be a second place deciding what a pack
 * is called, and the two would drift the first time the route changed.
 */
/** Hex SHA-256 of the bytes, or `null` where the platform crypto is unavailable.
 *
 * **The lib types say `crypto.subtle` is always there and it is not.** It is undefined outside a
 * secure context — plain HTTP on a hostname — so the guard is a runtime fact the type checker does not
 * model, and the cast is what makes the check expressible rather than a dead conditional it would
 * strip. Reading it unguarded would turn a working download into a TypeError.
 */
async function sha256(bytes: ArrayBuffer): Promise<string | null> {
  const subtle = (globalThis.crypto as Crypto | undefined)?.subtle;
  if (subtle === undefined) return null;
  try {
    /* A view rather than the buffer itself. `Response.arrayBuffer()` can hand back a buffer from
     * another realm — jsdom does — and `SubtleCrypto.digest` then refuses it as "not an instance of
     * ArrayBuffer". A `Uint8Array` over it is accepted everywhere. */
    const digest = await subtle.digest('SHA-256', new Uint8Array(bytes));
    return [...new Uint8Array(digest)]
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    /* **A failed verification must never cost the file.** This is a report *about* a download that
     * has already happened, so anything the platform crypto refuses degrades to "not verified" —
     * the alternative is that a digest quirk turns a successful download into a reported failure,
     * which is what the first version of this did. */
    return null;
  }
}

/** Fetch one pack's bytes with the caller's credentials and hand them to the browser to save.
 *
 * **Never blocks on a mismatch.** A caller investigating a discrepancy needs the bytes most when
 * something is wrong with them, so the file is saved and the mismatch is reported. Refusing to hand
 * over the evidence because the evidence looks wrong is the opposite of useful.
 */
export async function downloadPack(pack: EvidencePack): Promise<DownloadOutcome> {
  /* Through `getBytes`, so the bearer token is attached and a refusal arrives as the server's own
     problem document — a 403 here is a permission answer, not a failed download. */
  const { bytes, headers } = await getBytes(downloadUrl(pack.id));

  /* **Saved before it is verified, and the order is the point.** The bytes are the deliverable and
   * the digest is a report on them, so nothing in the verification can stand between a caller and
   * their file — including a mismatch, which is when they need it most.
   *
   * Saved by handing the browser a blob URL and clicking a detached anchor. `download` carries the
   * server's filename; the URL is revoked immediately after, because an un-revoked blob URL pins the
   * whole pack in memory for the life of the document. */
  const url = URL.createObjectURL(
    new Blob([bytes], { type: headers.get('content-type') ?? 'application/json' }),
  );
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    /* The fallback is composed here rather than inside the shared reader: that helper takes a
       *name*, where the old local one took an id and built the name itself — so passing `pack.id`
       through would have saved an extensionless UUID whenever the header was missing. */
    anchor.download = filenameFrom(
      headers.get('content-disposition'),
      `evidence-pack-${pack.id}.json`,
    );
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }

  const digest = await sha256(bytes);
  if (digest === null) return 'unverified';
  return digest === pack.checksum ? 'verified' : 'mismatched';
}

export interface CoverageView {
  readonly items: readonly DatasetCoverage[];
  readonly truncated: boolean;
  /** The server's own sentence saying this is not a compliance judgement. Rendered verbatim. */
  readonly disclaimer: string;
}

function isCoverage(value: unknown): value is {
  readonly items: readonly DatasetCoverage[];
  readonly meta: {
    readonly truncated: boolean;
    readonly not_a_compliance_verdict: string;
  };
} {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  const items = candidate['items'];
  const meta = candidate['meta'];
  if (!Array.isArray(items) || !items.every(isDatasetCoverage)) return false;
  if (typeof meta !== 'object' || meta === null) return false;
  const shape = meta as Record<string, unknown>;
  /* Both required, and the disclaimer especially: the contract makes it required so a client cannot
   * render this table without it, and a guard that let it default would be the one place that
   * defeats the point. */
  return (
    typeof shape['truncated'] === 'boolean' &&
    typeof shape['not_a_compliance_verdict'] === 'string'
  );
}

/** What this platform observed per dataset, for one framework version.
 *
 * Read only when a framework is open — `enabled` on both halves of its identity, because a framework
 * is `(code, version)` and a request with either missing would ask about a framework that is not one.
 */
export function useCoverage(
  code: string | null,
  version: string | null,
): UseQueryResult<CoverageView> {
  return useQuery({
    queryKey: ['compliance', 'coverage', code, version],
    enabled: code !== null && version !== null && code !== '' && version !== '',
    queryFn: async () => {
      const view = await getOne(
        `${FRAMEWORKS_URL}/${encodeURIComponent(code ?? '')}/${encodeURIComponent(
          version ?? '',
        )}/coverage`,
        isCoverage,
        'framework coverage',
      );
      return {
        items: view.items,
        truncated: view.meta.truncated,
        disclaimer: view.meta.not_a_compliance_verdict,
      };
    },
    retry: false,
  });
}
